import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { isSuperAdmin } from "../../utils/roles";
import { playOnboardingSound } from "../../utils/onboardingSound";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  yellow: "#FFC857",
  green: "#26A269",
};

function MetricCard({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
          </Box>
        </Stack>
        {helper && <Typography sx={{ mt: 2, color: "rgba(25,36,47,0.6)" }}>{helper}</Typography>}
      </CardContent>
    </Card>
  );
}

function FirstProjectWelcome({ user, onStart }) {
  const steps = [
    {
      icon: <StorefrontRoundedIcon />,
      title: "Poné el nombre",
      text: "Decinos cómo se llama tu negocio y elegimos su dirección inicial.",
      color: BRAND.orange,
      background: "rgba(243,90,58,.11)",
    },
    {
      icon: <WebRoundedIcon />,
      title: "Personalizá",
      text: "Podés sumar tu logo ahora o avanzar y editar todo más adelante.",
      color: BRAND.purple,
      background: "rgba(105,87,232,.11)",
    },
    {
      icon: <CloudDoneRoundedIcon />,
      title: "Publicamos",
      text: "WebProfit crea la web, la conecta y te entrega el dominio automáticamente.",
      color: BRAND.green,
      background: "rgba(38,162,105,.11)",
    },
  ];

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 4, md: 6 },
          p: { xs: 3, sm: 4, md: 6 },
          color: "#fff",
          background: "linear-gradient(125deg, #19242F 0%, #293D54 48%, #6957E8 100%)",
          boxShadow: "0 24px 70px rgba(25,36,47,.18)",
          "@keyframes onboardingFloat": {
            "0%, 100%": { transform: "translateY(0) rotate(-2deg)" },
            "50%": { transform: "translateY(-9px) rotate(2deg)" },
          },
        }}
      >
        <Box sx={{ position: "absolute", width: 260, height: 260, borderRadius: "50%", bgcolor: "rgba(255,200,87,.16)", right: -70, top: -95 }} />
        <Box sx={{ position: "absolute", width: 170, height: 170, borderRadius: "50%", bgcolor: "rgba(243,90,58,.22)", right: 180, bottom: -95 }} />

        <Grid container spacing={4} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Chip
              icon={<AutoAwesomeRoundedIcon />}
              label="TU PRIMER PROYECTO ESTÁ INCLUIDO"
              sx={{ bgcolor: "rgba(255,255,255,.14)", color: "#fff", fontWeight: 950, letterSpacing: .4, mb: 2.5, "& .MuiChip-icon": { color: BRAND.yellow } }}
            />
            <Typography variant="h2" sx={{ fontSize: { xs: "2.35rem", sm: "3.15rem", md: "4rem" }, lineHeight: .98, fontWeight: 1000, letterSpacing: -2 }}>
              ¡Activá tu proyecto web gratis!
            </Typography>
            <Typography sx={{ mt: 2.25, maxWidth: 650, color: "rgba(255,255,255,.78)", fontSize: { xs: "1rem", md: "1.12rem" }, lineHeight: 1.65 }}>
              Hola {user?.name || ""}. No necesitás saber de diseño ni programación: te guiamos, creamos la estructura y dejamos tu página lista para que empieces a editarla.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mt: 3.5 }}>
              <Button
                size="large"
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={onStart}
                sx={{
                  bgcolor: BRAND.orange,
                  borderRadius: 999,
                  px: 3.5,
                  py: 1.45,
                  textTransform: "none",
                  fontWeight: 1000,
                  fontSize: "1rem",
                  boxShadow: "0 14px 34px rgba(243,90,58,.34)",
                  "&:hover": { bgcolor: "#dd472b", transform: "translateY(-2px)" },
                  transition: "all .2s ease",
                }}
              >
                Empezar ahora
              </Button>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.67)", fontWeight: 750 }}>
                Sin tarjeta · Configuración guiada
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ display: "grid", placeItems: "center", minHeight: { xs: 220, md: 300 } }}>
              <Paper
                elevation={0}
                sx={{
                  width: { xs: 210, sm: 245 },
                  p: 2.5,
                  borderRadius: 5,
                  bgcolor: "rgba(255,255,255,.96)",
                  color: BRAND.dark,
                  transform: "rotate(-2deg)",
                  animation: "onboardingFloat 4s ease-in-out infinite",
                  boxShadow: "0 28px 65px rgba(0,0,0,.25)",
                }}
              >
                <Box sx={{ width: 60, height: 60, borderRadius: 4, display: "grid", placeItems: "center", bgcolor: "rgba(255,200,87,.25)", color: "#BE7B00", mb: 2 }}>
                  <RocketLaunchRoundedIcon sx={{ fontSize: 34 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 1000 }}>Tu web despega acá</Typography>
                <Stack spacing={1.2} sx={{ mt: 2 }}>
                  {["Dominio automático", "Landing editable", "Base preparada"].map((label) => (
                    <Stack key={label} direction="row" spacing={1} alignItems="center">
                      <CheckCircleRoundedIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 850 }}>{label}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box>
        <Typography variant="h4" sx={{ fontWeight: 1000, color: BRAND.dark, textAlign: "center" }}>
          Son solo 3 pasos
        </Typography>
        <Typography sx={{ color: "rgba(25,36,47,.62)", textAlign: "center", mt: .6 }}>
          Vos cargás lo esencial. WebProfit hace el trabajo técnico.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {steps.map((step, index) => (
          <Grid item xs={12} md={4} key={step.title}>
            <Paper elevation={0} sx={{ height: "100%", p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)", bgcolor: "#fff" }}>
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", color: step.color, bgcolor: step.background }}>
                  {step.icon}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 1000, color: step.color, letterSpacing: .8 }}>PASO {index + 1}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 1000, color: BRAND.dark }}>{step.title}</Typography>
                </Box>
              </Stack>
              <Typography sx={{ mt: 1.7, color: "rgba(25,36,47,.64)", lineHeight: 1.65 }}>{step.text}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export default function DashboardHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject, projectCreation, loadProjects, loading, error } = useProjects();
  const superAdmin = isSuperAdmin(user);
  const isFirstProjectState = !superAdmin && !loading && projects.length === 0;

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const startOnboarding = () => {
    playOnboardingSound("start");
    navigate("/dashboard/proyectos");
  };

  if (isFirstProjectState) {
    return (
      <>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FirstProjectWelcome user={user} onStart={startOnboarding} />
      </>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
              Dashboard
            </Typography>
            <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
              Hola {user?.name}. {superAdmin ? "Esta es tu entrada al superpanel de WebProfit." : "Este es el panel de tu proyecto dentro de WebProfit."}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {superAdmin && (
              <Button variant="outlined" onClick={() => navigate("/dashboard/superpanel")} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3 }}>
                Superpanel
              </Button>
            )}
            <Button variant="contained" onClick={() => navigate("/dashboard/proyectos")} sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3 }}>
              {superAdmin
                ? "Crear / ver proyectos"
                : projectCreation?.canCreate
                  ? "Agregar proyecto"
                  : "Ver mis proyectos"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2.5}>
        {superAdmin && (
          <Grid item xs={12} md={4}>
            <MetricCard icon={<AdminPanelSettingsRoundedIcon />} title="Modo" value="Superadmin" helper="Podés aprobar usuarios, crear clientes y configurar planes mensuales." />
          </Grid>
        )}
        <Grid item xs={12} md={superAdmin ? 4 : 4}>
          <MetricCard icon={<StorefrontRoundedIcon />} title={superAdmin ? "Proyectos totales" : "Mis proyectos"} value={loading ? "..." : projects.length} helper="Cada proyecto puede tener su propia DB y configuración." />
        </Grid>
        <Grid item xs={12} md={superAdmin ? 4 : 4}>
          <MetricCard icon={<Inventory2RoundedIcon />} title="Proyecto activo" value={activeProject?.name || "Ninguno"} helper="El panel opera sobre el proyecto seleccionado." />
        </Grid>
        {!superAdmin && (
          <Grid item xs={12} md={4}>
            <MetricCard icon={<SettingsRoundedIcon />} title="Acceso" value="Cliente" helper="Ves solo las secciones habilitadas para tu proyecto." />
          </Grid>
        )}
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                {superAdmin ? "Orden de trabajo del superpanel" : "Tu operación dentro de WebProfit"}
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 1 }}>
                {superAdmin
                  ? "Primero aprobás usuarios, después creás o asignás proyectos, y finalmente definís el plan mensual de cada cliente."
                  : "Desde acá vas a poder administrar productos, ver estadísticas y configurar lo que WebProfit tenga habilitado para tu proyecto."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {superAdmin && <Chip icon={<PeopleAltRoundedIcon />} label="Usuarios" color="success" />}
              <Chip label="Proyectos" color="success" />
              <Chip label="Productos" color="success" />
              <Chip label="Estadísticas después" variant="outlined" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
